import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

// ─── Constants ──────────────────────────────────────────────────────────────
const PREVIEW_TIMEOUT_MS = 3 * 60 * 1000;       // 3 min soft reservation
const PROCESSING_TIMEOUT_MS = 15 * 60 * 1000;   // 15 min after copy

// Rate limiting: max claims per browser/IP within window
const RATE_LIMIT_MAX_CLAIMS = 2;
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000;     // 10 minutes

// Browser-side rate limit storage key
const LOCAL_CLAIMS_KEY = "uoh_recent_claims";

// Unsupported regions: Unity does not function in these countries
// Source: Unity FAQ + README. Visitors get a polite message instead of a code.
const UNSUPPORTED_COUNTRY_CODES = new Set([
  "AE", // United Arab Emirates
  "OM", // Oman
  "CN", // China
  "IR", // Iran
  "KP", // North Korea
  "SY", // Syria
  "RU", // Russia
  "AF", // Afghanistan
  "BY", // Belarus
  "CU", // Cuba
  "MM", // Myanmar
  "SD", // Sudan
  "SS", // South Sudan
  "VE", // Venezuela
  "YE", // Yemen
  "ZW", // Zimbabwe
]);

// Occupied Ukrainian regions (region-level check on Ukrainian visitors)
const UNSUPPORTED_UA_REGIONS = new Set([
  "Crimea", "Sevastopol", "Donetsk", "Donetsk Oblast", "Luhansk", "Luhansk Oblast",
  "Crimea Republic", "Autonomous Republic of Crimea",
]);

function isRegionBlocked(info) {
  if (!info || !info.country_code) return false;
  if (UNSUPPORTED_COUNTRY_CODES.has(info.country_code.toUpperCase())) return true;
  if (info.country_code.toUpperCase() === "UA" && info.region && UNSUPPORTED_UA_REGIONS.has(info.region)) return true;
  return false;
}

// Fire-and-forget interaction tracking. Writes one row to the events table.
// Never blocks the UI and never throws — tracking failures are silent so they
// can't break the user experience. `context` carries optional ref_code +
// coarse location captured elsewhere in the app.
async function trackEvent(eventType, context = {}) {
  try {
    await supabase.from("events").insert({
      event_type: eventType,
      ref_code: context.refCode || null,
      country: context.country || null,
      country_code: context.countryCode || null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : null,
    });
  } catch { /* tracking is best-effort; never surface errors */ }
}

// Read recent claim timestamps from localStorage
function getRecentClaimsLocal() {
  try {
    const raw = localStorage.getItem(LOCAL_CLAIMS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const cutoff = Date.now() - RATE_LIMIT_WINDOW_MS;
    return arr.filter(t => typeof t === "number" && t > cutoff);
  } catch { return []; }
}

function recordClaimLocal() {
  try {
    const recent = getRecentClaimsLocal();
    recent.push(Date.now());
    localStorage.setItem(LOCAL_CLAIMS_KEY, JSON.stringify(recent));
  } catch { /* localStorage may be disabled */ }
}

// ─── Supabase helpers ───────────────────────────────────────────────────────

async function fetchAll(query) {
  const PAGE = 1000;
  let all = [];
  let from = 0;
  while (true) {
    const { data, error } = await query.range(from, from + PAGE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

// Fast: grab a small batch in one query, pick randomly client-side
async function fetchOneCode() {
  const { data, error } = await supabase.from("codes").select("id, code").eq("status", "available").limit(20);
  if (error) throw error;
  if (!data || data.length === 0) return null;
  return data[Math.floor(Math.random() * data.length)];
}

async function fetchAllCodes() {
  return fetchAll(supabase.from("codes").select("id, code, status, created_at, claimed_at, processing_since, disputed_at, dispute_note").order("created_at", { ascending: true }));
}

// Soft reservation: visitor sees the code but hasn't engaged yet (3 min lock)
async function moveToPreviewing(id) {
  const { error } = await supabase.from("codes").update({ status: "previewing", processing_since: new Date().toISOString() }).eq("id", id);
  if (error) throw error;
}

// Upgrade soft reservation to full processing once visitor copies (15 min lock)
async function upgradeToProcessing(id) {
  const { error } = await supabase.from("codes").update({ status: "processing", processing_since: new Date().toISOString() }).eq("id", id).eq("status", "previewing");
  if (error) throw error;
}

async function confirmClaim(id, refCode) {
  const update = { status: "used", claimed_at: new Date().toISOString(), processing_since: null };
  if (refCode) update.ref_code = refCode;
  const { error } = await supabase.from("codes").update(update).eq("id", id);
  if (error) throw error;
}

async function restoreCodeById(id) {
  const { error } = await supabase.from("codes").update({ status: "available", claimed_at: null, processing_since: null }).eq("id", id);
  if (error) throw error;
}

async function flagCodeAsDisputed(id, note) {
  const { error } = await supabase.from("codes").update({ status: "disputed", disputed_at: new Date().toISOString(), dispute_note: note || null }).eq("id", id);
  if (error) throw error;
}

// Expire both preview (3 min) and processing (15 min) reservations back to available
async function expireProcessing() {
  const previewCutoff = new Date(Date.now() - PREVIEW_TIMEOUT_MS).toISOString();
  const processingCutoff = new Date(Date.now() - PROCESSING_TIMEOUT_MS).toISOString();
  await supabase.from("codes").update({ status: "available", processing_since: null }).eq("status", "previewing").lt("processing_since", previewCutoff);
  await supabase.from("codes").update({ status: "available", processing_since: null }).eq("status", "processing").lt("processing_since", processingCutoff);
}

// ─── Self-service referrals ─────────────────────────────────────────────────
// Visitors create their own referral link (no account needed) and return to
// the #referrals tab any time to check clicks + claims. Both calls go through
// SECURITY DEFINER functions (see referral-self-service-migration.sql), so the
// public role never gains direct write access to the referrals table or read
// access to visits/claimed codes.

const MY_REFERRAL_KEY = "uoh_my_referral";

// Generated referral links always use the canonical domain. Both domains run
// the same site against the same database, so clicks and claims through EITHER
// domain count toward the same stats — older unity-op.com links keep working
// and keep counting, while every newly created link points at u-operator.com.
const CANONICAL_SITE = "https://u-operator.com";

function loadMyReferral() {
  try {
    const raw = localStorage.getItem(MY_REFERRAL_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && obj.code && obj.slug ? obj : null;
  } catch { return null; }
}

function saveMyReferral(ref) {
  try { localStorage.setItem(MY_REFERRAL_KEY, JSON.stringify(ref)); } catch { /* ok */ }
}

function clearMyReferral() {
  try { localStorage.removeItem(MY_REFERRAL_KEY); } catch { /* ok */ }
}

async function rpcCreateReferral(name, channels, shortCode) {
  const { data, error } = await supabase.rpc("create_referral", { p_name: name, p_channels: channels, p_code: shortCode });
  if (error) throw error;
  return data; // {ok, code, slug, name} or {ok:false, reason, message}
}

async function rpcReferralStats(key) {
  const { data, error } = await supabase.rpc("get_referral_stats", { p_key: key });
  if (error) throw error;
  return data; // {found, name, code, slug, created_at, clicks, claimed}
}

// ─── Styles ────────────────────────────────────────────────────────────────
const FONT = ``; // Fonts loaded via index.html for faster rendering

const css = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0d1520; }
  ::selection { background: #fff533; color: #000; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #131a26; }
  ::-webkit-scrollbar-thumb { background: #2a3a4e; }

  .shell { min-height: 100vh; background: linear-gradient(180deg, #020305 0%, #070a10 8%, #0d1520 25%, #0d1520 100%); font-family: 'DM Mono', monospace; color: #e8e4dc; position: relative; }
  .shell::after { content: ''; position: fixed; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; z-index: 9999; opacity: 0.025; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }

  .tab-btn { background: none; border: none; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 11px; letter-spacing: 0.2em; padding: 10px 20px; text-transform: uppercase; transition: all 0.2s; }
  .tab-btn.active { color: #fff533; border-bottom: 2px solid #fff533; }
  .tab-btn:not(.active) { color: #556070; border-bottom: 2px solid transparent; }
  .tab-btn:not(.active):hover { color: #8899aa; }

  .claim-btn { background: #fff533; color: #0d1520; border: none; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 800; font-size: 15px; letter-spacing: 0.2em; text-transform: uppercase; padding: 26px 56px; line-height: 1.6; transition: background 0.15s, opacity 0.15s; border-radius: 4px; }
  .claim-btn:hover:not(:disabled) { background: #d4f56a; }
  .claim-btn:active:not(:disabled) { background: #bde050; }
  .claim-btn:disabled { background: #1a2230; color: #445060; cursor: not-allowed; }

  .confirm-btn { background: transparent; color: #fff533; border: 2px solid #fff533; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 800; font-size: 13px; letter-spacing: 0.15em; text-transform: uppercase; padding: 14px 36px; transition: all 0.15s; border-radius: 4px; }
  .confirm-btn:hover:not(:disabled) { background: rgba(200,240,96,0.08); transform: translateY(-1px); }
  .confirm-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .copy-btn { background: #152030; border: 1px solid #2a3a4e; color: #99aabb; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; padding: 8px 16px; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px; border-radius: 4px; }
  .copy-btn:hover { border-color: #fff533; color: #fff533; }
  .copy-btn.copied { border-color: #fff533; color: #fff533; background: rgba(200,240,96,0.06); animation: copyFlash 0.4s ease; }
  @keyframes copyFlash { 0% { transform: scale(1); } 20% { transform: scale(1.08); box-shadow: 0 0 12px rgba(255,245,51,0.3); } 100% { transform: scale(1); } }

  .admin-input { background: #131a26; border: 1px solid #2a3a4e; color: #e8e4dc; font-family: 'DM Mono', monospace; font-size: 13px; padding: 10px 14px; width: 100%; outline: none; transition: border 0.2s; border-radius: 4px; }
  .admin-input:focus { border-color: #fff533; }

  .admin-btn { background: #152030; border: 1px solid #2a3a4e; color: #e8e4dc; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; padding: 10px 20px; transition: all 0.15s; border-radius: 4px; }
  .admin-btn:hover { background: #1c2736; border-color: #3a4a5e; }

  .flag-btn { background: transparent; border: 1px solid #2e1a40; color: #7a4a9a; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; padding: 7px 14px; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px; border-radius: 4px; }
  .flag-btn:hover { border-color: #c080ff; color: #c080ff; background: rgba(180,80,255,0.05); }
  .flag-btn:disabled { opacity: 0.35; cursor: not-allowed; }
  .flag-sent { border-color: #c080ff !important; color: #c080ff !important; background: rgba(180,80,255,0.07) !important; }

  .accordion { border: 1px solid #1c2736; margin-bottom: 8px; border-radius: 4px; overflow: hidden; }
  .accordion-header { width: 100%; background: #131a26; border: none; cursor: pointer; display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; color: #99aabb; transition: color 0.15s; text-align: left; }
  .accordion-header:hover { color: #fff; }
  .accordion-header.open { color: #fff533; }
  .accordion-chevron { font-size: 10px; transition: transform 0.2s; flex-shrink: 0; }
  .accordion-chevron.open { transform: rotate(180deg); }
  .accordion-body { padding: 16px 18px 20px; background: #0d1520; border-top: 1px solid #1c2736; font-size: 13px; color: #8899aa; line-height: 1.9; font-family: 'Syne', sans-serif; font-weight: 400; }
  .accordion-body ol, .accordion-body ul { padding-left: 18px; }
  .accordion-body li { margin-bottom: 8px; }
  .accordion-body strong { color: #ccd0d8; }

  .info-link-btn { background: none; border: none; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #fff533; padding: 6px 0; transition: color 0.15s; text-decoration: none; }
  .info-link-btn:hover { color: #d4f56a; }

  .step-row { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
  .step-card { border: 1px solid #1c2736; background: #131a26; cursor: pointer; transition: all 0.3s ease, max-height 0.4s ease, padding 0.3s ease; overflow: hidden; min-width: 0; border-radius: 4px; }
  .step-card:hover { border-color: #2a3a4e; }
  .step-card.collapsed { width: 100%; padding: 14px 18px; display: flex; flex-direction: row; align-items: center; gap: 10px; max-height: 60px; }
  .step-card.collapsed:hover { border-color: rgba(255,245,51,0.3); }
  .step-card.expanded { flex: 1 1 0%; padding: 20px; max-height: 800px; }
  .step-card.completed { border-color: #1a2a1a; }
  .step-card.active-step { border-color: #fff533; border-top: 2px solid #fff533; }
  .step-card-num { font-family: 'DM Mono', monospace; font-weight: 500; font-size: 20px; color: #fff533; line-height: 1; }
  .step-card-num.done { color: #4ade80; font-size: 16px; }
  .step-card-label { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #556070; text-align: left; line-height: 1.2; }
  .step-card-label.done { color: #2a6a3a; }
  .step-expanded-title { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 13px; color: #e8e4dc; letter-spacing: 0.04em; margin-bottom: 6px; }
  .step-expanded-desc { font-size: 12px; color: #8899aa; line-height: 1.6; margin-bottom: 10px; }
  .detail-toggle { background: none; border: none; cursor: pointer; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: #556070; padding: 4px 0; margin-top: 8px; transition: color 0.15s; }
  .detail-toggle:hover { color: #fff533; }

  html { -webkit-text-size-adjust: 100%; }
  @media (max-width: 600px) {
    .tab-btn { font-size: 10px; letter-spacing: 0.12em; padding: 12px 14px; }
    .claim-btn { font-size: 14px; letter-spacing: 0.14em; padding: 22px 24px; }
    .confirm-btn { font-size: 12px; padding: 16px 20px; }
    .admin-btn { font-size: 10px; padding: 10px 14px; }
    .copy-btn { font-size: 10px; padding: 9px 14px; }
    .accordion-header { font-size: 11px; padding: 13px 14px; }
    .accordion-body { padding: 14px 14px 18px; }
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(200,240,96,0.4); } 50% { box-shadow: 0 0 0 12px rgba(200,240,96,0); } }
  @keyframes codeGlow { 0%,100% { box-shadow: 0 0 20px rgba(255,245,51,0.06), 0 0 40px rgba(255,245,51,0.03); } 50% { box-shadow: 0 0 28px rgba(255,245,51,0.12), 0 0 56px rgba(255,245,51,0.05); } }
  .fade-in { animation: fadeIn 0.4s ease; }
  .code-reveal { animation: pulse 1s ease; }
  .code-glow { animation: codeGlow 3s ease-in-out infinite; }
`;

// Responsive privacy-enhanced YouTube embed (16:9). Uses youtube-nocookie.com
// so no tracking cookies load until the visitor presses play.
function VideoEmbed({ id, title, start }) {
  return (
    <div>
      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000", border: "1px solid #1c2736", borderRadius: 4, overflow: "hidden" }}>
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}${start ? `?start=${start}` : ""}`}
          title={title}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </div>
      <div style={{ marginTop: 6, textAlign: "right" }}>
        <a href={`https://www.youtube.com/watch?v=${id}${start ? `&t=${start}s` : ""}`} target="_blank" rel="noreferrer"
          style={{ fontSize: 10, color: "#556070", textDecoration: "none", letterSpacing: "0.06em" }}>
          Trouble playing? Watch on YouTube ↗
        </a>
      </div>
    </div>
  );
}

function Accordion({ title, children, defaultOpen = false, onOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => {
    setOpen(o => {
      // Fire onOpen only on the open transition (closed → open), not on close.
      if (!o && typeof onOpen === "function") onOpen();
      return !o;
    });
  };
  return (
    <div className="accordion">
      <button className={`accordion-header ${open ? "open" : ""}`} onClick={toggle}>
        <span>{title}</span>
        <span className={`accordion-chevron ${open ? "open" : ""}`}>▼</span>
      </button>
      {open && <div className="accordion-body fade-in">{children}</div>}
    </div>
  );
}

export default function DistributionApp() {
  const [noCodesAvailable, setNoCodesAvailable] = useState(false);
  const [limitedRegion, setLimitedRegion] = useState(false);
  // refCode + refResolved have ref-mirror copies because trackVisit runs inside
  // a useEffect that only sees the initial closure values. Reading from refs
  // ensures we always log with the latest resolved ref_code.
  const [refCode, setRefCode] = useState(null);
  const [refResolved, setRefResolved] = useState(false);
  const refCodeRef = useRef(null);
  const refResolvedRef = useRef(false);
  useEffect(() => { refCodeRef.current = refCode; }, [refCode]);
  useEffect(() => { refResolvedRef.current = refResolved; }, [refResolved]);

  // Simple shortcuts for standalone PDFs (no tracking, no full-screen viewer).
  // /#scout and /#runner redirect to the clean paths /scout and /runner, which
  // vercel.json rewrites to the actual PDF files. To add another: drop the PDF
  // in public/, add a rewrite in vercel.json, and add a line here.
  useEffect(() => {
    const SIMPLE_PDF_LINKS = { "#scout": "/scout", "#runner": "/runner" };
    const hash = window.location.hash.toLowerCase();
    const target = SIMPLE_PDF_LINKS[hash];
    if (target) window.location.replace(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire a single guide-view event when arriving via a guide link.
  // Runs once on mount. The view counts toward User Guide engagement only.
  useEffect(() => {
    if (!guideMode) return;
    trackEvent("guide_view", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    const queryRef = new URLSearchParams(window.location.search).get("ref");
    if (queryRef) {
      setRefCode(queryRef);
      setRefResolved(true);
      return;
    }
    // Match /r/<slug> path. Resolve to underlying ref code via referrals table.
    const slugMatch = path.match(/^\/r\/([^/?#]+)/i);
    if (slugMatch) {
      const slug = slugMatch[1].toLowerCase();
      supabase.from("referrals").select("code").eq("slug", slug).limit(1)
        .then(({ data }) => {
          if (data && data.length > 0) setRefCode(data[0].code);
          setRefResolved(true);
        })
        .catch(() => setRefResolved(true));
      // Safety: unblock visit logging after 4 seconds even if Supabase hangs.
      setTimeout(() => setRefResolved(true), 4000);
    } else {
      setRefResolved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState(null);

  // Visitor identity (set by trackVisit async, used for rate limiting)
  const [visitorInfo, setVisitorInfo] = useState(null);
  // Rate limit state
  const [rateLimitedUntil, setRateLimitedUntil] = useState(null);
  const [rateLimitMessage, setRateLimitMessage] = useState("");

  // Initial tab: supports direct-info links via ?info=1, ?tab=info, or #info,
  // and direct-referral links via ?tab=referrals or #referrals. When arriving
  // on Info or Referrals, the auto-fetch effect (gated on tab === "claim")
  // skips reserving a code from the pool. Visitors can browse freely; clicking
  // the Claim tab afterwards triggers normal code reservation.
  const [tab, setTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const hash = window.location.hash.toLowerCase();
      const fromInfoLink = params.get("info") === "1" || params.get("tab") === "info" || hash === "#info";
      const fromReferralLink = params.get("tab") === "referrals" || hash === "#referrals" || hash === "#refs";
      if (fromReferralLink) return "referrals";
      return fromInfoLink ? "info" : "claim";
    } catch { return "claim"; }
  });

  // Guide-link mode: shows the User Guide PDF full-screen and logs a single
  // guide-view event. For trackable guide links you post externally. Does NOT
  // touch the claim pipeline or count as a referral; only adds to the guide
  // view count. Triggered by any of these (all equivalent):
  //   /guide   /ug         (clean paths, best for sharing)
  //   #guide   #ug         (hash variants)
  //   ?guide=1             (query variant)
  const [guideMode, setGuideMode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const path = window.location.pathname.replace(/\/+$/, "").toLowerCase(); // strip trailing slash
      const hash = window.location.hash.toLowerCase();
      return (
        params.get("guide") === "1" ||
        path === "/guide" || path === "/ug" ||
        hash === "#guide" || hash === "#ug"
      );
    } catch { return false; }
  });

  // Mobile browsers (iOS Safari, Android Chrome) cannot render a PDF inside an
  // iframe — they show a blank/black frame or silently trigger a download.
  // This is a long-standing browser limitation, not a site bug, so on small
  // screens we show clear Open/Download actions instead of an empty viewer.
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.matchMedia("(max-width: 820px)").matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(max-width: 820px)"); } catch { return; }
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  // Combined claim flow
  const [activeCode, setActiveCode] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [autoFetched, setAutoFetched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  // Flag flow
  const [flagging, setFlagging] = useState(false);
  const [flagSent, setFlagSent] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);

  // Honeypot field: bots fill every input on a page; real users won't.
  // If this gets filled, we silently treat the claim as fraudulent.
  const honeypotRef = useRef("");
  const [flagNote, setFlagNote] = useState("");

  // Step wizard
  const [activeStep, setActiveStep] = useState(1);
  const [showDetail1, setShowDetail1] = useState(false);
  const [showDetail2, setShowDetail2] = useState(false);
  const [showDetail3, setShowDetail3] = useState(false);

  // Waitlist
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState("");
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);

  // Self-service referrals (#referrals tab)
  const [myRef, setMyRef] = useState(() => loadMyReferral());
  const [refStats, setRefStats] = useState(null);
  const [refStatsLoading, setRefStatsLoading] = useState(false);
  const [refName, setRefName] = useState("");
  const [refChannels, setRefChannels] = useState("");
  const [refShort, setRefShort] = useState("");
  const [refCreating, setRefCreating] = useState(false);
  const [refFormError, setRefFormError] = useState("");
  const [lookupKey, setLookupKey] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [refLinkCopied, setRefLinkCopied] = useState(false);

  const refreshRefStats = async (key) => {
    if (!key) return;
    setRefStatsLoading(true);
    try {
      const s = await rpcReferralStats(key);
      if (s && s.found) setRefStats(s);
      else setRefStats(null);
    } catch { /* keep prior stats on transient errors */ }
    setRefStatsLoading(false);
  };

  // Load this visitor's referral stats whenever they open the Referrals tab.
  useEffect(() => {
    if (tab === "referrals" && myRef?.code) refreshRefStats(myRef.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleCreateReferral = async () => {
    const name = refName.trim();
    const channels = refChannels.trim();
    const short = refShort.trim();
    if (name.length < 2 || name.length > 40) {
      setRefFormError("Please enter a name between 2 and 40 characters.");
      return;
    }
    if (channels.length < 2 || channels.length > 60) {
      setRefFormError("Tell us where you plan to share the link (e.g. YouTube, Discord, family).");
      return;
    }
    if (short.length < 3 || short.length > 24) {
      setRefFormError("Short code must be 3–24 characters — letters, numbers, dashes, underscores.");
      return;
    }
    setRefCreating(true);
    setRefFormError("");
    try {
      const res = await rpcCreateReferral(name, channels, short);
      if (res && res.ok) {
        const ref = { code: res.code, slug: res.slug, name: res.name };
        saveMyReferral(ref);
        setMyRef(ref);
        setRefStats({ found: true, ...ref, clicks: 0, claimed: 0 });
        setRefName("");
        setRefChannels("");
        setRefShort("");
        trackEvent("referral_created", { slug: res.slug });
      } else {
        setRefFormError(res?.message || "Could not create your link. Please try again.");
      }
    } catch {
      setRefFormError("Could not create your link. Please try again in a moment.");
    }
    setRefCreating(false);
  };

  const handleLookup = async () => {
    const key = lookupKey.trim();
    if (!key) return;
    setLookupBusy(true);
    setLookupError("");
    try {
      const s = await rpcReferralStats(key);
      if (s && s.found) {
        const ref = { code: s.code, slug: s.slug, name: s.name };
        saveMyReferral(ref);
        setMyRef(ref);
        setRefStats(s);
        setLookupKey("");
      } else {
        setLookupError("No referral link found for that code or name. Check the spelling and try again.");
      }
    } catch {
      setLookupError("Lookup failed. Please try again in a moment.");
    }
    setLookupBusy(false);
  };

  const referralUrl = myRef ? `${CANONICAL_SITE}/r/${myRef.slug}` : "";

  const copyRefLink = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
    } catch {
      const el = document.createElement("textarea");
      el.value = referralUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setRefLinkCopied(true);
    setTimeout(() => setRefLinkCopied(false), 2000);
  };

  useEffect(() => {
    // Initial load: one fetch, show code instantly, everything else in background
    const init = async () => {
      try {
        expireProcessing().catch(() => {});
        const code = await fetchOneCode();
        setDbError(null);
        if (code) {
          setAutoFetched(true);
          setActiveCode({ id: code.id, code: code.code, confirmed: false });
          moveToPreviewing(code.id).catch(() => {});
        } else {
          setNoCodesAvailable(true);
        }
      } catch (e) { setDbError(e.message); }
      setLoading(false);
    };
    init();

    // Multi-provider geo lookup with fallbacks. Tries providers in order,
    // normalizing response shapes. If all fail, returns null (silent).
    const fetchGeoWithFallback = async () => {
      const providers = [
        // Provider 1: ipapi.co
        {
          url: "https://ipapi.co/json/",
          parse: (d) => ({
            ip: d.ip, country: d.country_name, country_code: d.country_code,
            region: d.region, city: d.city,
          }),
        },
        // Provider 2: ipwho.is
        {
          url: "https://ipwho.is/",
          parse: (d) => d.success === false ? null : ({
            ip: d.ip, country: d.country, country_code: d.country_code,
            region: d.region, city: d.city,
          }),
        },
        // Provider 3: api.country.is: country-only, no IP
        {
          url: "https://api.country.is/",
          parse: (d) => ({
            ip: d.ip || null, country: null, country_code: d.country,
            region: null, city: null,
          }),
        },
      ];

      for (const p of providers) {
        try {
          const res = await fetch(p.url, { signal: AbortSignal.timeout ? AbortSignal.timeout(4000) : undefined });
          if (!res.ok) continue;
          const data = await res.json();
          const parsed = p.parse(data);
          if (parsed && parsed.country_code) return parsed;
        } catch (_e) { /* try next provider */ }
      }
      return null;
    };

    // Compute a stable visitor identifier (hash of IP + UA, no PII stored).
    // Used to track first-time vs returning visitors for analytics.
    const computeVisitorId = async (ip, ua) => {
      const raw = `${ip || "unknown"}|${ua || "unknown"}`;
      try {
        const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
        return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
      } catch {
        // Fallback if SubtleCrypto isn't available
        let h = 0;
        for (let i = 0; i < raw.length; i++) h = ((h << 5) - h + raw.charCodeAt(i)) | 0;
        return Math.abs(h).toString(36);
      }
    };

    // Track visit in background (non-blocking, no PII stored)
    // Also captures IP + country into local state for rate-limiting + region checks.
    const trackVisit = async () => {
      try {
        const info = await fetchGeoWithFallback();
        if (info) {
          setVisitorInfo(info);

          // Visitor session upsert: first-time vs returning analytics
          const visitorId = await computeVisitorId(info.ip, navigator.userAgent);
          try {
            const { data: existing } = await supabase.from("visitor_sessions")
              .select("visitor_id, visit_count").eq("visitor_id", visitorId).limit(1);
            if (existing && existing.length > 0) {
              // Returning visitor: bump count + last_seen
              await supabase.from("visitor_sessions")
                .update({ last_seen: new Date().toISOString(), visit_count: existing[0].visit_count + 1 })
                .eq("visitor_id", visitorId);
            } else {
              // First-time visitor
              await supabase.from("visitor_sessions").insert({
                visitor_id: visitorId,
                country_code: info.country_code,
                first_seen: new Date().toISOString(),
                last_seen: new Date().toISOString(),
                visit_count: 1,
              });
            }
          } catch { /* non-critical */ }

          // Only log a 'visit' row once per session (separate from rate-limit data).
          // Wait for refCode resolution first so /r/slug visits get attributed
          // correctly. Polls every 50ms up to a 4-second cap, then proceeds
          // either way (the safety setTimeout above guarantees this terminates).
          const sessionKey = "uoh_visit_logged";
          if (!sessionStorage.getItem(sessionKey)) {
            const start = Date.now();
            while (!refResolvedRef.current && Date.now() - start < 4500) {
              await new Promise(r => setTimeout(r, 50));
            }
            await supabase.from("visits").insert({
              country: info.country,
              country_code: info.country_code,
              region: info.region,
              city: info.city,
              ref_code: refCodeRef.current,
              user_agent: navigator.userAgent.slice(0, 200),
            });
            sessionStorage.setItem(sessionKey, "1");
          }
        }
      } catch (_e) { /* silent */ }
      // Mark resolved so auto-fetch can proceed (server still does its own geo check)
      setGeoResolved(true);
    };
    setTimeout(trackVisit, 100); // run sooner so claim isn't blocked for long

    // Failsafe: if geo APIs hang, unblock auto-fetch after 5 seconds.
    // The server-side claim Edge Function does its own region check as backup.
    const geoFailsafe = setTimeout(() => setGeoResolved(true), 5000);

    const subTimer = setTimeout(() => {
      const channel = supabase.channel("dist-changes").on("postgres_changes", { event: "*", schema: "public", table: "codes" }, () => {}).subscribe();
      subTimer._channel = channel;
    }, 1500);
    return () => {
      clearTimeout(subTimer);
      clearTimeout(geoFailsafe);
      if (subTimer._channel) supabase.removeChannel(subTimer._channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When visitor info comes in, check if their region is blocked.
  // Region check is now a soft WARNING, not a block. Visitors in regions with
  // limited Unetwork task availability see a heads-up banner but can still
  // claim and run a license — their reservation is never cancelled.
  useEffect(() => {
    if (!visitorInfo) return;
    if (isRegionBlocked(visitorInfo)) setLimitedRegion(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorInfo]);

  // Track whether geo lookup has completed (success OR failure)
  const [geoResolved, setGeoResolved] = useState(false);

  // Auto-fetch for subsequent tab switches (not initial load).
  // Wait for geo lookup to complete (or fail) so the limited-region warning
  // can render alongside the code rather than after it.
  useEffect(() => {
    if (!geoResolved) return;
    if (!loading && !autoFetched && !activeCode && !fetching && tab === "claim") {
      setAutoFetched(true);
      getCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, autoFetched, activeCode, fetching, tab, geoResolved]);

  const getCode = async () => {
    setFetching(true);
    try {
      const code = await fetchOneCode();
      if (!code) { setNoCodesAvailable(true); setFetching(false); return; }
      setNoCodesAvailable(false);
      setActiveCode({ id: code.id, code: code.code, confirmed: false });
      setFlagSent(false);
      setShowFlagModal(false);
      setActiveStep(1);
      setShowDetail1(false);
      setShowDetail2(false);
      setShowDetail3(false);
      moveToPreviewing(code.id).catch(() => {});
    } catch (e) { alert("Error: " + e.message); }
    setFetching(false);
  };

  const doConfirmClaim = async () => {
    if (!activeCode) return;
    setConfirming(true);

    // Soft client-side rate limit hint, friendlier UX. The server is authoritative.
    // The Edge Function does the real check using IP and database.
    const browserCount = getRecentClaimsLocal().length;
    if (browserCount >= RATE_LIMIT_MAX_CLAIMS) {
      const recent = getRecentClaimsLocal();
      const oldest = recent.length > 0 ? Math.min(...recent) : Date.now();
      const waitUntil = oldest + RATE_LIMIT_WINDOW_MS;
      const minutes = Math.max(1, Math.ceil((waitUntil - Date.now()) / 60000));
      setRateLimitedUntil(waitUntil);
      setRateLimitMessage(`You've recently claimed ${browserCount} code${browserCount !== 1 ? "s" : ""}. Please wait about ${minutes} minute${minutes !== 1 ? "s" : ""} before claiming another.`);
      setConfirming(false);
      return;
    }

    try {
      // Call the server-side Edge Function: handles honeypot, rate limit,
      // region check, and atomic status update in one place.
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/claim-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${supabaseAnonKey}`,
          "apikey": supabaseAnonKey,
        },
        body: JSON.stringify({
          code_id: activeCode.id,
          ref_code: refCode,
          honeypot: honeypotRef.current || "",
          client_country_code: visitorInfo?.country_code || null,
          client_country: visitorInfo?.country || null,
          client_region: visitorInfo?.region || null,
        }),
      });

      const result = await res.json();

      if (res.ok && result.ok && result.claimed) {
        // Successful claim
        setActiveCode({ ...activeCode, confirmed: true });
        recordClaimLocal();
      } else if (result.reason === "rate_limited") {
        const minutes = result.waitMinutes || 10;
        setRateLimitedUntil(Date.now() + minutes * 60 * 1000);
        setRateLimitMessage(result.message || `Please wait ${minutes} minutes before claiming another.`);
      } else if (result.reason === "region_blocked") {
        // Older claim-code function versions hard-blocked limited regions.
        // Show the warning banner; once the updated function is deployed,
        // this branch never fires.
        setLimitedRegion(true);
        alert("Your region has limited availability right now. Please try again in a few minutes.");
      } else if (result.reason === "code_not_available") {
        alert(result.message || "This code is no longer available.");
        setActiveCode(null);
        setTimeout(() => getCode(), 300);
      } else {
        alert("Could not claim code. Please try again.");
      }
    } catch (e) {
      // Network or function error: fall back to direct database call so the
      // user isn't stuck if the Edge Function is unreachable.
      try {
        await confirmClaim(activeCode.id, refCode);
        setActiveCode({ ...activeCode, confirmed: true });
        recordClaimLocal();
      } catch (e2) {
        alert("Error: " + e2.message);
      }
    }
    setConfirming(false);
  };

  const submitFlag = async () => {
    setFlagging(true);
    try {
      let targetId = activeCode?.id;
      if (activeCode?.confirmed) {
        const all = await fetchAllCodes();
        const match = all.find(c => c.code === activeCode.code && c.status === "used");
        if (match) targetId = match.id;
      }
      if (targetId) { await flagCodeAsDisputed(targetId, flagNote.trim() || null);  }
      setFlagSent(true); setShowFlagModal(false); setFlagNote("");
    } catch (e) { alert("Error: " + e.message); }
    setFlagging(false);
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); } catch {
      const el = document.createElement("textarea"); el.value = code; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    if (activeStep === 1) setActiveStep(2);
    // Upgrade soft preview reservation to full processing lock
    if (activeCode && !activeCode.confirmed) {
      upgradeToProcessing(activeCode.id).catch(() => {});
    }
  };

  // Detect manual copy (Ctrl/Cmd+C or right-click + copy on the code text)
  // Same upgrade behavior as clicking the Copy button.
  useEffect(() => {
    if (!activeCode || activeCode.confirmed) return;
    const handleManualCopy = (e) => {
      // Only fire if the selection contains the active code text
      const selection = window.getSelection();
      if (!selection) return;
      const selectedText = selection.toString();
      if (selectedText && activeCode.code && selectedText.includes(activeCode.code)) {
        upgradeToProcessing(activeCode.id).catch(() => {});
      }
    };
    document.addEventListener("copy", handleManualCopy);
    return () => document.removeEventListener("copy", handleManualCopy);
  }, [activeCode]);

  // Auto-clear rate limit message when the wait window passes
  useEffect(() => {
    if (!rateLimitedUntil) return;
    const remaining = rateLimitedUntil - Date.now();
    if (remaining <= 0) {
      setRateLimitedUntil(null);
      setRateLimitMessage("");
      return;
    }
    const timer = setTimeout(() => {
      setRateLimitedUntil(null);
      setRateLimitMessage("");
    }, remaining);
    return () => clearTimeout(timer);
  }, [rateLimitedUntil]);

  const tryDifferentCode = async () => {
    if (!activeCode) return;
    await restoreCodeById(activeCode.id);
    setActiveCode(null);
    setTimeout(() => getCode(), 300);
  };

  const submitWaitlist = async () => {
    const email = waitlistEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setWaitlistError("Please enter a valid email."); return; }
    setWaitlistSubmitting(true); setWaitlistError("");
    try {
      await supabase.from("settings").upsert({ key: "waitlist:" + email, value: new Date().toISOString() });
      setWaitlistSubmitted(true);
    } catch { setWaitlistError("Something went wrong."); }
    finally { setWaitlistSubmitting(false); }
  };

  if (loading) return (
    <div className="shell" style={{ minHeight: "100vh" }}>
      <style>{FONT + css + `
        @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        .skel { background: linear-gradient(90deg, #131a26 25%, #1a2230 50%, #131a26 75%); background-size: 800px 100%; animation: shimmer 1.6s ease infinite; border-radius: 2px; }
      `}</style>
      <div style={{ background: "#020305", height: 56 }} />
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "28px 16px" }}>
        <div className="skel" style={{ height: 80, marginBottom: 24 }} />
        <div className="skel" style={{ height: 160, marginBottom: 24 }} />
        <div className="skel" style={{ height: 48, marginBottom: 8 }} />
        <div className="skel" style={{ height: 48, marginBottom: 8 }} />
        <div className="skel" style={{ height: 48 }} />
      </div>
    </div>
  );

  if (dbError) return (
    <div className="shell" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 32 }}>
      <style>{FONT + css}</style>
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#ff4444", marginBottom: 16, textTransform: "uppercase" }}>Connection error</div>
        <div style={{ fontSize: 13, color: "#8899aa", lineHeight: 1.8 }}>{dbError}</div>
      </div>
    </div>
  );

  // Full-screen guide view — shown when arriving via a ?guide=1 / #guide link.
  // Renders the PDF filling the viewport with a "Back to Site" button that
  // drops the user into the normal app. The guide-view event already fired
  // in the mount effect above.
  if (guideMode) return (
    <div className="shell" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{FONT + css}</style>
      <div style={{ background: "#020305", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/unity-logo.png" alt="Unity" style={{ width: 30, height: 25, objectFit: "contain" }} />
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 13, letterSpacing: "0.08em", color: "#fff", textTransform: "uppercase" }}>
            User Guide
          </span>
        </div>
        <button
          onClick={() => {
            // Clear any guide marker from the URL (path, hash, or query) so the
            // cleaned URL lands on the normal site if refreshed or shared, then
            // drop into the app. Rewrites the path to "/" if it was /guide or /ug.
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete("guide");
              const h = url.hash.toLowerCase();
              if (h === "#guide" || h === "#ug") url.hash = "";
              const p = url.pathname.replace(/\/+$/, "").toLowerCase();
              if (p === "/guide" || p === "/ug") url.pathname = "/";
              window.history.replaceState({}, "", url.toString());
            } catch { /* non-fatal */ }
            setGuideMode(false);
          }}
          style={{ background: "transparent", border: "1px solid #2a3a4e", color: "#fff533", padding: "8px 16px", fontSize: 11, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", borderRadius: 4, cursor: "pointer" }}>
          ← Back to Site
        </button>
      </div>
      <div style={{ flex: 1, background: "#0d1520", minHeight: 0 }}>
        {isMobile ? (
          <div style={{ padding: "40px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: 40 }}>📄</div>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 15, letterSpacing: "0.08em", color: "#fff", textTransform: "uppercase" }}>
              User Guide
            </div>
            <p style={{ fontSize: 13, color: "#8899aa", lineHeight: 1.8, maxWidth: 360 }}>
              Mobile browsers can't display PDFs inside a page. Tap below to open the guide in your phone's PDF viewer.
            </p>
            <a href="/ug.pdf" target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#fff533", color: "#0d1520", padding: "14px 28px", fontSize: 13, fontFamily: "Syne, sans-serif", fontWeight: 800, letterSpacing: "0.1em", textDecoration: "none", textTransform: "uppercase", borderRadius: 4 }}>
              📄 Open Guide
            </a>
            <a href="/ug.pdf" download
              style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "transparent", border: "1px solid #2a3a4e", color: "#8899aa", padding: "12px 24px", fontSize: 12, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.1em", textDecoration: "none", textTransform: "uppercase", borderRadius: 4 }}>
              ⬇ Download PDF
            </a>
          </div>
        ) : (
          <iframe
            src="/ug.pdf#view=FitH"
            title="User Guide"
            width="100%"
            height="100%"
            style={{ border: "none", display: "block", minHeight: "calc(100vh - 46px)" }}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="shell">
      <style>{FONT + css}</style>

      {/* Header */}
      <div style={{ background: "#020305" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", height: 56, padding: "0 16px" }}>
          <button onClick={() => { setTab("claim"); setActiveCode(null); setAutoFetched(false); }}
            style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, padding: 0 }}>
            <img src="/unity-logo.png" alt="Unity" style={{ width: 38, height: 32, objectFit: "contain" }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
              <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 15, letterSpacing: "0.1em", color: "#fff", textTransform: "uppercase" }}>
                Unity Operator Hub
              </span>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: "0.08em", color: "#fff533", marginTop: 4 }}>
                Uniting Unetwork Resources
              </span>
            </div>
          </button>
        </div>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex" }}>
          <button className={`tab-btn ${tab === "claim" ? "active" : ""}`} style={{ flex: 1 }} onClick={() => setTab("claim")}>Claim</button>
          <button className={`tab-btn ${tab === "info" ? "active" : ""}`} style={{ flex: 1 }} onClick={() => setTab("info")}>Info</button>
          <button className={`tab-btn ${tab === "referrals" ? "active" : ""}`} style={{ flex: 1 }} onClick={() => setTab("referrals")}>Referrals</button>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 16px" }}>

        {/* ══════════ CLAIM TAB ══════════ */}
        {tab === "claim" && (
          <div style={{ maxWidth: 520, margin: "0 auto", padding: "4px 0 32px" }}>

            {/* Region warning: limited task availability — claiming still allowed */}
            {limitedRegion && (
              <div className="fade-in" style={{ padding: "22px 24px", background: "#1a1408", border: "1px solid #3a2d12", borderRadius: 4, marginBottom: 20 }}>
                <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 13, letterSpacing: "0.15em", color: "#fbbf24", textTransform: "uppercase", marginBottom: 12 }}>⚠ Limited Tasks Available in Your Region</div>
                <p style={{ fontSize: 13, color: "#aab0c0", lineHeight: 1.8, marginBottom: 14 }}>
                  Unetwork currently has limited task availability in <strong style={{ color: "#ddd" }}>{visitorInfo?.country || "your region"}</strong>, so earnings may be lower than the averages shown until more tasks roll out to your area. You can still claim a license, run the app, and earn from the tasks that are available.
                </p>
                <p style={{ fontSize: 12, color: "#8899aa", lineHeight: 1.7, marginBottom: 14 }}>
                  Using a VPN? Disable it and refresh the page — VPNs interfere with task delivery and can block earnings entirely.
                </p>
                <p style={{ fontSize: 11, color: "#556070", lineHeight: 1.7 }}>
                  Want a heads-up when more tasks reach your region? Use the email signup below.
                </p>
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #3a2d12" }}>
                  {waitlistSubmitted ? (
                    <div style={{ fontSize: 12, color: "#fbbf24" }}>✓ You are on the list.</div>
                  ) : (
                    <>
                      <input className="admin-input" type="email" placeholder="your@email.com" value={waitlistEmail}
                        onChange={e => { setWaitlistEmail(e.target.value); setWaitlistError(""); }}
                        onKeyDown={e => e.key === "Enter" && submitWaitlist()} style={{ marginBottom: 10, fontSize: 12 }} />
                      {waitlistError && <div style={{ fontSize: 11, color: "#ff4444", marginBottom: 8 }}>{waitlistError}</div>}
                      <button className="claim-btn" style={{ width: "100%", padding: "12px 20px", fontSize: 11 }} onClick={submitWaitlist} disabled={waitlistSubmitting}>
                        {waitlistSubmitting ? "Submitting..." : "Notify Me"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Loading state (initial auto-fetch or manual fetch) */}
            {(fetching && !activeCode) && (
              <div className="fade-in" style={{ textAlign: "center", padding: "40px 0" }}>
                <span style={{ display: "inline-block", width: 20, height: 20, border: "2px solid #1c2736", borderTopColor: "#fff533", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                <div style={{ marginTop: 14, fontSize: 12, color: "#556070", letterSpacing: "0.1em" }}>Finding the best available license...</div>
              </div>
            )}

            {/* No codes available */}
            {!fetching && !activeCode && noCodesAvailable && (
              <div className="fade-in">
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 15, letterSpacing: "0.15em", color: "#556070", textTransform: "uppercase" }}>No Licenses Available</div>
                </div>
                <div style={{ padding: "20px", background: "#131a26", border: "1px solid #1c2736", marginBottom: 20, borderRadius: 4 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#fff533", textTransform: "uppercase", marginBottom: 8 }}>Get Notified</div>
                  <p style={{ fontSize: 12, color: "#8899aa", lineHeight: 1.7, marginBottom: 14 }}>No codes available right now. Enter your email and we will notify you when new licenses are added.</p>
                  {waitlistSubmitted ? (
                    <div style={{ fontSize: 12, color: "#fff533" }}>✓ You're on the list.</div>
                  ) : (
                    <>
                      <input className="admin-input" type="email" placeholder="your@email.com" value={waitlistEmail}
                        onChange={e => { setWaitlistEmail(e.target.value); setWaitlistError(""); }}
                        onKeyDown={e => e.key === "Enter" && submitWaitlist()} style={{ marginBottom: 10, fontSize: 12 }} />
                      {waitlistError && <div style={{ fontSize: 11, color: "#ff4444", marginBottom: 8 }}>{waitlistError}</div>}
                      <button className="claim-btn" style={{ width: "100%", padding: "14px 20px", fontSize: 12 }} onClick={submitWaitlist} disabled={waitlistSubmitting}>
                        {waitlistSubmitting ? "Submitting..." : "Notify Me"}
                      </button>
                    </>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "#8899aa", lineHeight: 1.8, textAlign: "center" }}>
                  Claim a Unetwork License Operator code and start earning daily passive income. Setup takes less than 5 minutes.
                </div>
              </div>
            )}

            {/* ── Unified single page ── */}
            {activeCode && (
              <div className="fade-in">

                {/* Compact info card: intro + earnings + learn more */}
                <div style={{ padding: "16px 18px", background: "#131a26", border: "1px solid #1c2736", borderLeft: "3px solid #fff533", marginBottom: 24, borderRadius: 4 }}>
                  {/* Passive earnings row */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 20, color: "#fff533", fontWeight: 500 }}>$6–$12</span>
                      <span style={{ fontSize: 11, color: "#8899aa" }}>/mo passive · per phone</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#556070", marginTop: 2 }}>
                      Multiple phones stack: 5 phones ≈ <span style={{ color: "#fff533" }}>$50/mo</span>
                    </div>
                  </div>

                  {/* Active task earnings row: stacked so "Up to $1,350" never collides with the description on small screens */}
                  <div style={{ marginBottom: 12, paddingTop: 8, borderTop: "1px dashed #1c2736" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, color: "#4ade80", fontWeight: 500 }}>Up to $1,350</span>
                      <span style={{ fontSize: 11, color: "#8899aa" }}>/mo</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#556070", marginTop: 2 }}>
                      with opt-in active tasks
                    </div>
                  </div>

                  <div style={{ fontSize: 12, color: "#8899aa", lineHeight: 1.6, marginBottom: 8 }}>Earn real USD by helping verify global telecom networks. Just install the app and let it run. Setup takes under 5 minutes.</div>
                  <button className="info-link-btn" onClick={() => setTab("info")} style={{ fontSize: 9 }}>How does it work? →</button>
                </div>

                {/* Separator */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                  <div style={{ flex: 1, height: 1, background: "#1c2736" }} />
                  <span style={{ fontSize: 9, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#3a4a5a" }}>Your code is ready</span>
                  <div style={{ flex: 1, height: 1, background: "#1c2736" }} />
                </div>

                {/* Hero code card */}
                <div className={activeCode.confirmed ? "" : "code-glow"} style={{ border: `1px solid ${activeCode.confirmed ? "#fff533" : "#1c2736"}`, background: activeCode.confirmed ? "#0e1a12" : "#0a1018", padding: "36px 28px 32px", marginBottom: 24, textAlign: "center", borderRadius: 4 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.2em", color: activeCode.confirmed ? "#6a8a30" : "#556070", textTransform: "uppercase", marginBottom: 18 }}>
                    {activeCode.confirmed ? "License Claimed" : "Your License Code"}
                  </div>
                  <div className={activeCode.confirmed ? "code-reveal" : ""} style={{ fontFamily: "DM Mono, monospace", fontSize: "clamp(20px, 6vw, 32px)", letterSpacing: "0.12em", color: "#fff", fontWeight: 300, marginBottom: 24, wordBreak: "break-all" }}>
                    {activeCode.code}
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
                    <button className={`copy-btn ${copied ? "copied" : ""}`} onClick={() => copyCode(activeCode.code)}>
                      {copied ? "✓ Copied" : "⧉ Copy Code"}
                    </button>
                    {!activeCode.confirmed ? (
                      <button className="confirm-btn" style={{ padding: "8px 24px", fontSize: 11, opacity: rateLimitedUntil && Date.now() < rateLimitedUntil ? 0.5 : 1 }} onClick={doConfirmClaim} disabled={confirming || (rateLimitedUntil && Date.now() < rateLimitedUntil)}>
                        {confirming ? "Confirming..." : (rateLimitedUntil && Date.now() < rateLimitedUntil ? "✕ Rate Limited" : "✓ Claim License")}
                      </button>
                    ) : (
                      <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 11, color: "#4ade80", letterSpacing: "0.12em", textTransform: "uppercase", padding: "8px 24px" }}>✓ The license is now yours</span>
                    )}
                  </div>

                  {/* Honeypot: hidden from humans, bots fill all inputs */}
                  <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }} aria-hidden="true">
                    <label htmlFor="email_confirmation_uoh">Leave this empty</label>
                    <input
                      type="text"
                      id="email_confirmation_uoh"
                      name="email_confirmation"
                      tabIndex={-1}
                      autoComplete="off"
                      onChange={(e) => { honeypotRef.current = e.target.value; }}
                    />
                  </div>

                  {/* Rate limit warning */}
                  {rateLimitMessage && !activeCode.confirmed && (
                    <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(180,80,255,0.06)", border: "1px solid #2e1a40", borderRadius: 4, fontSize: 11, color: "#c080ff", lineHeight: 1.6, textAlign: "center" }}>
                      {rateLimitMessage}
                    </div>
                  )}
                  {/* Secondary actions */}
                  <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16, paddingTop: 14, borderTop: "1px solid #1c2736" }}>
                    {!activeCode.confirmed && (
                      <>
                        <button onClick={tryDifferentCode} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: "#3a4a5a", fontFamily: "Syne, sans-serif", fontWeight: 600, letterSpacing: "0.06em", transition: "color 0.15s" }} onMouseEnter={e => e.target.style.color = "#8899aa"} onMouseLeave={e => e.target.style.color = "#3a4a5a"}>↻ Different Code</button>
                        <button onClick={() => setShowFlagModal(true)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: "#3a4a5a", fontFamily: "Syne, sans-serif", fontWeight: 600, letterSpacing: "0.06em", transition: "color 0.15s" }} onMouseEnter={e => e.target.style.color = "#7a4a9a"} onMouseLeave={e => e.target.style.color = "#3a4a5a"}>⚑ Report</button>
                      </>
                    )}
                    {activeCode.confirmed && (
                      <>
                        {!flagSent ? (
                          <button onClick={() => setShowFlagModal(true)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: "#3a4a5a", fontFamily: "Syne, sans-serif", fontWeight: 600, letterSpacing: "0.06em", transition: "color 0.15s" }} onMouseEnter={e => e.target.style.color = "#7a4a9a"} onMouseLeave={e => e.target.style.color = "#3a4a5a"}>⚑ Code Not Working?</button>
                        ) : (
                          <span style={{ fontSize: 10, color: "#7a4a9a" }}>✓ Report submitted</span>
                        )}
                        <button onClick={getCode} disabled={noCodesAvailable || fetching} style={{ background: "none", border: "none", cursor: noCodesAvailable ? "not-allowed" : "pointer", fontSize: 10, color: "#fff533", fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.06em", opacity: noCodesAvailable ? 0.4 : 1 }}>{fetching ? "Finding..." : "+ Another License"}</button>
                      </>
                    )}
                  </div>
                </div>

                {/* Step wizard cards */}
                <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#fff533", textTransform: "uppercase", marginBottom: 10 }}>3 Steps to Start Earning</div>
                <div className="step-row">

                  {/* Step 1: Copy */}
                  {activeStep === 1 ? (
                    <div className="step-card expanded active-step">
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <span className="step-card-num">1</span>
                        <span className="step-expanded-title" style={{ marginBottom: 0 }}>Copy Your Code</span>
                      </div>
                      <div className="step-expanded-desc">Tap the "Copy Code" button above. You will paste this into the Unetwork App in the next step.</div>
                      <button className="detail-toggle" onClick={() => setShowDetail1(!showDetail1)}>
                        {showDetail1 ? "Hide details ▲" : "More details ▼"}
                      </button>
                      {showDetail1 && (
                        <div className="fade-in" style={{ marginTop: 10, padding: "12px 14px", background: "#0d1520", border: "1px solid #1c2736", borderRadius: 4, fontSize: 12, color: "#8899aa", lineHeight: 1.8 }}>
                          <ul style={{ paddingLeft: 16 }}>
                            <li>Your code is unique to this session and reserved for 15 minutes.</li>
                            <li>If you do not claim it within that window, it returns to the pool for others.</li>
                            <li>You can use the "Try Different Code" button below if this one does not work.</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`step-card collapsed ${activeStep > 1 ? "completed" : ""}`} onClick={() => setActiveStep(1)}>
                      <span className={`step-card-num ${activeStep > 1 ? "done" : ""}`}>{activeStep > 1 ? "✓" : "1"}</span>
                      <span className={`step-card-label ${activeStep > 1 ? "done" : ""}`}>Copy</span>
                    </div>
                  )}

                  {/* Step 2: Download */}
                  {activeStep === 2 ? (
                    <div className="step-card expanded active-step">
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <span className="step-card-num">2</span>
                        <span className="step-expanded-title" style={{ marginBottom: 0 }}>Download and Enter Your Code</span>
                      </div>
                      <div className="step-expanded-desc">Install the Unetwork App, tap "Get Started," select "License Operator," tap "Enter a lease code," and paste your code.</div>
                      <div style={{ display: "flex", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                        <a href="https://play.google.com/store/apps/details?id=io.unetwork.app&hl=en" target="_blank" rel="noreferrer" onClick={() => setShowDetail2(true)} style={{ display: "inline-block", background: "#0d1520", border: "1px solid #2a3a4e", color: "#fff533", padding: "6px 14px", fontSize: 11, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.1em", textDecoration: "none", textTransform: "uppercase", borderRadius: 4 }}>▶ Google Play</a>
                        <a href="https://apps.apple.com/us/app/unetwork-app/id6755482738" target="_blank" rel="noreferrer" onClick={() => setShowDetail2(true)} style={{ display: "inline-block", background: "#0d1520", border: "1px solid #2a3a4e", color: "#fff533", padding: "6px 14px", fontSize: 11, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.1em", textDecoration: "none", textTransform: "uppercase", borderRadius: 4 }}> Apple App Store</a>
                      </div>
                      <button className="detail-toggle" onClick={() => setShowDetail2(!showDetail2)}>
                        {showDetail2 ? "Hide details ▲" : "More details ▼"}
                      </button>
                      {showDetail2 && (
                        <div className="fade-in" style={{ marginTop: 10, padding: "12px 14px", background: "#0d1520", border: "1px solid #1c2736", borderRadius: 4, fontSize: 12, color: "#8899aa", lineHeight: 1.8 }}>
                          <ol style={{ paddingLeft: 16 }}>
                            <li style={{ marginBottom: 6 }}>Open the app and tap <strong style={{ color: "#ccd0d8" }}>Get Started</strong>.</li>
                            <li style={{ marginBottom: 6 }}>You'll see three options — select <strong style={{ color: "#ccd0d8" }}>License Operator</strong>.</li>
                            <li style={{ marginBottom: 6 }}>Tap <strong style={{ color: "#ccd0d8" }}>Enter a lease code</strong>, paste your code, and tap <strong style={{ color: "#ccd0d8" }}>Continue</strong>.</li>
                            <li style={{ marginBottom: 6 }}><strong style={{ color: "#ff4444" }}>If you see "Failed to claim license":</strong> the code is already in use. Use the Report button below to flag it, then reserve a new one.</li>
                            <li style={{ marginBottom: 6 }}>Tap <strong style={{ color: "#ccd0d8" }}>Sign in with email</strong>, enter your email address, and tap <strong style={{ color: "#ccd0d8" }}>Continue</strong>. (Multiple licenses on multiple phones can share the same email, but each phone needs its own license.)</li>
                            <li style={{ marginBottom: 6 }}>Check your inbox for the verification code, paste it, and tap <strong style={{ color: "#ccd0d8" }}>Verify</strong>.</li>
                            <li style={{ marginBottom: 6 }}>Tap <strong style={{ color: "#ccd0d8" }}>Allow</strong> so the app can send you notifications.</li>
                            <li style={{ marginBottom: 6 }}>The app should bind your license <strong style={{ color: "#ccd0d8" }}>automatically</strong>. If you see <strong style={{ color: "#ccd0d8" }}>"No license selected"</strong> under your statuses, bind it manually: open the <strong style={{ color: "#ccd0d8" }}>menu</strong>, tap <strong style={{ color: "#ccd0d8" }}>Work</strong>, select your license, and tap <strong style={{ color: "#ccd0d8" }}>Continue</strong>.</li>
                            <li>Wait for <strong style={{ color: "#ccd0d8" }}>Service Status</strong>, <strong style={{ color: "#ccd0d8" }}>Switch Node</strong>, and <strong style={{ color: "#ccd0d8" }}>Validation Node</strong> to turn <strong style={{ color: "#fff533" }}>green</strong>.</li>
                          </ol>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`step-card collapsed ${activeStep > 2 ? "completed" : ""}`} onClick={() => setActiveStep(2)}>
                      <span className={`step-card-num ${activeStep > 2 ? "done" : ""}`}>{activeStep > 2 ? "✓" : "2"}</span>
                      <span className={`step-card-label ${activeStep > 2 ? "done" : ""}`}>Get App</span>
                    </div>
                  )}

                  {/* Step 3: Earn */}
                  {activeStep === 3 ? (
                    <div className="step-card expanded active-step">
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <span className="step-card-num">3</span>
                        <span className="step-expanded-title" style={{ marginBottom: 0 }}>Leave It Running and Earn</span>
                      </div>
                      <div className="step-expanded-desc">
                        Keep the app running in the background. You will earn approximately <strong style={{ color: "#fff533" }}>$6–$12/month</strong> per phone with no further action needed. Be sure to tap <strong style={{ color: "#ccd0d8" }}>Claim License</strong> above to secure your code.
                      </div>
                      <button className="detail-toggle" onClick={() => setShowDetail3(!showDetail3)}>
                        {showDetail3 ? "Hide details ▲" : "More details ▼"}
                      </button>
                      {showDetail3 && (
                        <div className="fade-in" style={{ marginTop: 10, padding: "12px 14px", background: "#0d1520", border: "1px solid #1c2736", borderRadius: 4, fontSize: 12, color: "#8899aa", lineHeight: 1.8 }}>
                          <ul style={{ paddingLeft: 16 }}>
                            <li style={{ marginBottom: 6 }}><strong style={{ color: "#ccd0d8" }}>Uptime is your reputation score.</strong> The longer your phone stays connected, the more tasks (and money) you receive. Above <strong style={{ color: "#fff533" }}>90%</strong> uptime brings a lot more tasks; below 80% you get far fewer. Check the <strong style={{ color: "#ccd0d8" }}>License tab</strong> in the app to see your current uptime.</li>
                            <li style={{ marginBottom: 6 }}>Check that all nodes are <strong style={{ color: "#fff533" }}>green</strong> every morning, midday, and before sleep for optimal results.</li>
                            <li style={{ marginBottom: 6 }}>The app uses up to 7GB of data per month. Stay connected to Wi-Fi as much as possible to avoid mobile data charges.</li>
                            <li style={{ marginBottom: 6 }}>Having a SIM card installed unlocks additional earning opportunities beyond the base passive task.</li>
                            <li style={{ marginBottom: 6 }}>Opt into <strong style={{ color: "#ccd0d8" }}>active tasks</strong> inside the app for additional income on top of the base rate.</li>
                            <li style={{ marginBottom: 6 }}>Old phones in a drawer? Each can run its own license — reserve one code per device.</li>
                            <li>Do not use a VPN or virtual machine. Either will interfere with connectivity and block earnings.</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="step-card collapsed" onClick={() => setActiveStep(3)}>
                      <span className="step-card-num">3</span>
                      <span className="step-card-label">Earn</span>
                    </div>
                  )}

                </div>

                {/* Install walkthrough video */}
                <div style={{ marginTop: 24, marginBottom: 4 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#8899aa", textTransform: "uppercase", marginBottom: 10 }}>▶ Watch: Full Install Walkthrough</div>
                  <VideoEmbed id="Xl2lbLb-Ykk" start={4} title="How to install the Unetwork App — step-by-step walkthrough" />
                </div>

                {/* Flag modal */}
                {showFlagModal && (
                  <div className="fade-in" style={{ marginTop: 16, padding: "20px 24px", background: "#1a1028", border: "1px solid #2e1a40", borderRadius: 4 }}>
                    <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#c080ff", textTransform: "uppercase", marginBottom: 12 }}>Report This Code</div>
                    <div style={{ fontSize: 12, color: "#8899aa", lineHeight: 1.8, marginBottom: 14 }}>
                      If this code is already in use or is not working, let us know.
                    </div>
                    <textarea className="admin-input" rows={3} placeholder="Optional: describe the issue"
                      value={flagNote} onChange={e => setFlagNote(e.target.value)}
                      style={{ fontSize: 12, lineHeight: 1.6, marginBottom: 12, background: "#150d1f", borderColor: "#2e1a40" }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="flag-btn flag-sent" style={{ flex: 1, justifyContent: "center" }} onClick={submitFlag} disabled={flagging}>
                        {flagging ? "Submitting..." : "Submit Report"}
                      </button>
                      <button className="admin-btn" style={{ fontSize: 10, padding: "10px 18px" }} onClick={() => { setShowFlagModal(false); setFlagNote(""); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {/* ══════════ INFO TAB ══════════ */}
        {tab === "info" && (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            {/* Unetwork overview video */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#8899aa", textTransform: "uppercase", marginBottom: 10 }}>▶ Watch: What Is Unetwork?</div>
              <VideoEmbed id="0Boz-vH70hA" title="Unetwork overview — how the network and earnings work" />
            </div>

            <Accordion title="💡 How You Earn Money" defaultOpen={true}>
              <p style={{ marginBottom: 14 }}>
                The world's telecommunications infrastructure carries billions of calls, messages, and data transfers every day. Over time, network routes degrade, leading to dropped connections, slow speeds, and failed deliveries. Identifying these issues at scale has traditionally required expensive, centralized phone farms that lack the reach to make a meaningful difference.
              </p>
              <p style={{ marginBottom: 14 }}>
                <strong style={{ color: "#fff533" }}>Unetwork</strong> solves this by distributing the work across thousands of real devices worldwide, forming the largest decentralized edge network of its kind. The Unetwork App runs quietly in the background of your phone, testing and validating network quality on behalf of telecom providers who pay for this service.
              </p>
              <p>
                You earn <strong style={{ color: "#fff533" }}>real USD</strong> for contributing your device. No action is required beyond keeping the app running.
              </p>
            </Accordion>

            <Accordion title="✓ Are You a Good Fit?">
              <p style={{ marginBottom: 10, color: "#ff6666" }}><strong>Not ideal if:</strong></p>
              <ul style={{ color: "#cc5555", marginBottom: 16 }}>
                <li>You rely on your phone as a mobile hotspot (the app uses up to 7GB/month).</li>
                <li>You have unreliable internet.</li>
                <li>You are in an unsupported country (UAE, China, Iran, North Korea, Syria, Russia, etc.).</li>
              </ul>
              <div style={{ paddingTop: 14, borderTop: "1px solid #1c2736" }}>
                <p style={{ marginBottom: 12, color: "#fff533" }}><strong>Good fit if you:</strong></p>
                <ul>
                  <li>Have a working smartphone (Android 7.1+ or iOS 16+ / iPhone 7 and newer).</li>
                  <li>Want genuine passive income with no surveys, no ads, and no recruiting.</li>
                  <li>Have a SIM card (optional but increases earnings).</li>
                </ul>
              </div>
            </Accordion>

            <Accordion title="⚡ What Happens After I Claim?">
              <ol>
                <li style={{ marginBottom: 10 }}>Download the Unetwork App and enter your code.</li>
                <li style={{ marginBottom: 10 }}>Connection is near-instant. Nodes turn green when working.</li>
                <li style={{ marginBottom: 10 }}>First payment appears within 24 hours, then accumulates daily.</li>
                <li style={{ marginBottom: 10 }}>
                  Passive earnings: approximately <strong style={{ color: "#fff533" }}>$6–$12/month</strong> per phone with no action needed beyond keeping the app running.
                </li>
                <li>Opt into active tasks inside the app to earn up to <strong style={{ color: "#4ade80" }}>$1,350/month</strong>. These are always optional but significantly increase your income.</li>
              </ol>
            </Accordion>

            <Accordion title="📋 Do's and Don'ts">
              <p style={{ marginBottom: 10 }}><strong style={{ color: "#fff533" }}>Do:</strong></p>
              <ul style={{ marginBottom: 16 }}>
                <li>Keep the Unetwork App running at all times.</li>
                <li>Maintain a stable internet connection.</li>
                <li>Disable automatic app closing in your phone's battery and background-app settings to keep Unetwork running 24/7.</li>
                <li><strong style={{ color: "#fff533" }}>Aim for 90%+ uptime.</strong> Higher uptime unlocks more rewards and additional task opportunities.</li>
                <li>Click "Claim This License" after setting up.</li>
                <li>Report non-working codes immediately.</li>
              </ul>
              <p style={{ marginBottom: 10 }}><strong style={{ color: "#ff4444" }}>Don't:</strong></p>
              <ul>
                <li>Share your license code.</li>
                <li>Use a VPN. It blocks earnings.</li>
                <li>Use a virtual machine.</li>
                <li>Let the app go offline frequently.</li>
              </ul>
            </Accordion>

            <Accordion title="💰 How to Cash Out">
              <p style={{ marginBottom: 14 }}>Choose one of the options below:</p>

              <div style={{ marginBottom: 14, padding: "12px 14px", background: "#131a26", border: "1px solid #1c2736", borderRadius: 4 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#ccd0d8", fontFamily: "Syne, sans-serif", fontWeight: 700, marginBottom: 6 }}>Web3 Wallet</div>
                <p>Minimum <strong style={{ color: "#fff533" }}>$5</strong> to withdraw. Go to <a href="https://manage.unetwork.io" target="_blank" rel="noreferrer" style={{ color: "#fff533", textDecoration: "none" }}>manage.unetwork.io</a> and follow the directions to connect your wallet and initiate a withdrawal.</p>
                <div style={{ marginTop: 10 }}>
                  <a href="https://youtu.be/ogJXdSTBNCA?si=6QolYAOkREBDoJF9" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#fff533", textDecoration: "none", fontSize: 11, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.08em" }}>▶ Watch Withdrawal Walkthrough</a>
                </div>
              </div>

              <div style={{ marginBottom: 14, padding: "12px 14px", background: "#131a26", border: "1px solid #1c2736", borderRadius: 4 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#ccd0d8", fontFamily: "Syne, sans-serif", fontWeight: 700, marginBottom: 6 }}>Bank Transfer</div>
                <p>Minimum <strong style={{ color: "#fff533" }}>$200</strong> to withdraw. Go to <a href="https://manage.unetwork.io" target="_blank" rel="noreferrer" style={{ color: "#fff533", textDecoration: "none" }}>manage.unetwork.io</a> and follow the withdrawal directions.</p>
                <a href="https://youtu.be/SgYjEdWF6wQ?si=PoZo7EnaUCRCJhou" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, color: "#fff533", textDecoration: "none", fontSize: 11, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.08em" }}>▶ Watch Withdrawal Walkthrough</a>
              </div>

              <div style={{ marginBottom: 14, padding: "12px 14px", background: "#131a26", border: "1px solid #1c2736", borderRadius: 4 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#ccd0d8", fontFamily: "Syne, sans-serif", fontWeight: 700, marginBottom: 6 }}>PayPal</div>
                <p><strong style={{ color: "#fff533" }}>No minimum</strong> to withdraw. Go to <a href="https://manage.unetwork.io" target="_blank" rel="noreferrer" style={{ color: "#fff533", textDecoration: "none" }}>manage.unetwork.io</a> and follow the withdrawal directions.</p>
              </div>

              <div style={{ padding: "12px 14px", background: "#131a26", border: "1px solid #1c2736", borderRadius: 4 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#ccd0d8", fontFamily: "Syne, sans-serif", fontWeight: 700, marginBottom: 6 }}>ZBCN Crypto Debit Card</div>
                <p>A partnership with a crypto company that deposits your earnings onto a debit card, allowing you to spend your Unetwork payouts directly. This option should be available soon.</p>
              </div>
            </Accordion>

            <Accordion title="ℹ️ About Unity Operator Hub">
              <p style={{ marginBottom: 14 }}>
                This platform connects you with available Unetwork Operator License codes, completely free, so you can use the Unetwork App to earn USD daily. No personal information is collected aside from an optional email address used to log in to the app.
              </p>
              <p style={{ marginBottom: 14 }}>
                This platform is free because we share in the revenue you generate. You keep <strong style={{ color: "#fff533" }}>70%–80% of all rewards</strong> (average market rate is 50%).
              </p>
              <p>
                We are not part of the Unetwork development team. We are an independent <strong style={{ color: "#ccd0d8" }}>Unetwork Node Operator (UNO)</strong> who owns and distributes license codes through this platform.
              </p>
            </Accordion>

            <Accordion title="📘 User Guide" onOpen={() => trackEvent("guide_open", { refCode: refCodeRef.current, country: visitorInfo?.country, countryCode: visitorInfo?.country_code })}>
              <p style={{ marginBottom: 14 }}>
                The <strong style={{ color: "#ccd0d8" }}>User Guide</strong> covers app installation, sign-in options (email and Web3), license binding, opt-in tasks, withdrawal instructions, dashboard navigation, troubleshooting, an FAQ, and resources.
              </p>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <a href="/ug.pdf" target="_blank" rel="noreferrer"
                  onClick={() => trackEvent("guide_newtab", { refCode: refCodeRef.current, country: visitorInfo?.country, countryCode: visitorInfo?.country_code })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#0d1520", border: "1px solid #2a3a4e", color: "#fff533", padding: "10px 16px", fontSize: 12, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.1em", textDecoration: "none", textTransform: "uppercase", borderRadius: 4 }}>
                  <span style={{ fontSize: 14 }}>📄</span> {isMobile ? "Open Guide" : "Open in New Tab"}
                </a>
                <a href="/ug.pdf" download
                  onClick={() => trackEvent("guide_download", { refCode: refCodeRef.current, country: visitorInfo?.country, countryCode: visitorInfo?.country_code })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "transparent", border: "1px solid #2a3a4e", color: "#8899aa", padding: "10px 16px", fontSize: 12, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.1em", textDecoration: "none", textTransform: "uppercase", borderRadius: 4 }}>
                  <span style={{ fontSize: 14 }}>⬇</span> Download PDF
                </a>
              </div>

              {/* Inline PDF viewer — desktop only. Mobile browsers render an
                  empty frame here, so we show a short note instead. */}
              {isMobile ? (
                <p style={{ fontSize: 11, color: "#556070", lineHeight: 1.7 }}>
                  Preview isn't available on mobile browsers — use the buttons above to read the guide.
                </p>
              ) : (
                <>
                  <div style={{ width: "100%", height: 600, border: "1px solid #1c2736", borderRadius: 4, overflow: "hidden", background: "#0d1520" }}>
                    <iframe
                      src="/ug.pdf#view=FitH"
                      title="User Guide"
                      width="100%"
                      height="100%"
                      style={{ border: "none", display: "block" }}
                    >
                      <p style={{ padding: 16, color: "#8899aa" }}>
                        Your browser cannot display PDFs inline. Please use the buttons above to open or download the guide.
                      </p>
                    </iframe>
                  </div>
                </>
              )}

              {!isMobile && (
                <p style={{ fontSize: 11, color: "#556070", lineHeight: 1.7, marginTop: 10 }}>
                  If the inline viewer does not load, tap "Open in New Tab" to view the guide.
                </p>
              )}
            </Accordion>

            <Accordion title="🔗 More Information">
              <p style={{ marginBottom: 14 }}>Official FAQ: <a href="https://unetwork.io/faq" target="_blank" rel="noreferrer" style={{ color: "#fff533", textDecoration: "none" }}>unetwork.io/faq</a></p>
              <p style={{ marginBottom: 8, color: "#8899aa" }}>Parent companies:</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <a href="https://worldmobile.io/" target="_blank" rel="noreferrer" style={{ color: "#fff533", textDecoration: "none", fontSize: 11 }}>World Mobile →</a>
                  <span style={{ fontSize: 10, color: "#8899aa", background: "#131a26", border: "1px solid #1c2736", padding: "2px 8px" }}>Trustpilot 4.6/5</span>
                </div>
                <a href="https://minutesnetwork.io/" target="_blank" rel="noreferrer" style={{ color: "#fff533", textDecoration: "none", fontSize: 11 }}>Minutes Network →</a>
              </div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #1c2736" }}>
                <p style={{ marginBottom: 8, color: "#8899aa" }}>Unetwork Operators YouTube:</p>
                <a href="https://www.youtube.com/@u-operator" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#fff533", textDecoration: "none", fontSize: 11, fontFamily: "Syne, sans-serif", fontWeight: 700, letterSpacing: "0.08em" }}>▶ Tutorial and informational videos for License Operators →</a>
              </div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #1c2736" }}>
                <p style={{ marginBottom: 8, color: "#8899aa" }}>Technical issues with the Unetwork App?</p>
                <p style={{ fontSize: 12, color: "#8899aa", lineHeight: 1.6 }}>
                  Email the official support team:&nbsp;
                  <a href="mailto:support@unetwork.io" style={{ color: "#fff533", textDecoration: "none", fontFamily: "DM Mono, monospace" }}>support@unetwork.io</a>
                </p>
              </div>
            </Accordion>

            <div style={{ marginTop: 28 }}>
              <button className="claim-btn" onClick={() => setTab("claim")} style={{ width: "100%" }}>
                Reserve a License
              </button>
            </div>
          </div>
        )}

        {/* ══════════ REFERRALS TAB ══════════ */}
        {tab === "referrals" && (
          <div className="fade-in" style={{ maxWidth: 560, margin: "0 auto", width: "100%" }}>

            {/* Intro */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#8899aa", marginBottom: 8, textTransform: "uppercase" }}>Share Your Link, Track Your Impact</div>
              <p style={{ fontSize: 13, color: "#aab0c0", lineHeight: 1.9 }}>
                Create your own referral link in seconds — no account needed. Share it anywhere, and every visit and claimed license through your link is counted. Come back to this page (<span style={{ fontFamily: "DM Mono, monospace", color: "#fff533", fontSize: 12 }}>{window.location.host}/#referrals</span>) any time to see your totals.
              </p>
            </div>

            {myRef ? (
              <>
                {/* Your link */}
                <div style={{ background: "#131a26", border: "1px solid #1c2736", borderRadius: 4, padding: "20px 22px", marginBottom: 16 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#8899aa", textTransform: "uppercase", marginBottom: 6 }}>Your Referral Link</div>
                  <div style={{ fontSize: 12, color: "#8899aa", marginBottom: 12 }}>Created by <strong style={{ color: "#ccd0d8" }}>{myRef.name}</strong></div>
                  <div style={{ fontFamily: "DM Mono, monospace", fontSize: "clamp(12px, 3.5vw, 15px)", letterSpacing: "0.04em", color: "#fff", background: "#0d1520", border: "1px solid #1c2736", borderRadius: 4, padding: "12px 14px", marginBottom: 12, wordBreak: "break-all" }}>
                    {referralUrl}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className={`copy-btn ${refLinkCopied ? "copied" : ""}`} onClick={copyRefLink}>
                      {refLinkCopied ? <>✓ Copied</> : <>⧉ Copy Link</>}
                    </button>
                    <button className="admin-btn" style={{ fontSize: 10 }} onClick={() => refreshRefStats(myRef.code)} disabled={refStatsLoading}>
                      {refStatsLoading ? "Refreshing…" : "↻ Refresh Stats"}
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: "#131a26", border: "1px solid #1c2736", borderRadius: 4, padding: "16px 18px", textAlign: "center" }}>
                    <div style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "#8899aa", marginBottom: 8 }}>Link Clicks</div>
                    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 28, color: "#fff" }}>
                      {refStatsLoading && !refStats ? "…" : (refStats?.clicks ?? 0)}
                    </div>
                  </div>
                  <div style={{ flex: 1, background: "#131a26", border: "1px solid #fff533", borderRadius: 4, padding: "16px 18px", textAlign: "center" }}>
                    <div style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "#8899aa", marginBottom: 8 }}>Licenses Claimed</div>
                    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 28, color: "#fff533" }}>
                      {refStatsLoading && !refStats ? "…" : (refStats?.claimed ?? 0)}
                    </div>
                  </div>
                </div>

                <div style={{ background: "#131a26", border: "1px solid #1c2736", borderRadius: 4, padding: "14px 18px", marginBottom: 20, fontSize: 12, color: "#8899aa", lineHeight: 1.8 }}>
                  <strong style={{ color: "#ccd0d8" }}>How counting works:</strong> a click is logged when someone opens your link. A claim is counted when they confirm a license code. Numbers update each time you refresh.
                </div>

                <button className="admin-btn" style={{ fontSize: 10 }}
                  onClick={() => { clearMyReferral(); setMyRef(null); setRefStats(null); }}>
                  Use a different link on this device
                </button>
              </>
            ) : (
              <>
                {/* Create */}
                <div style={{ background: "#131a26", border: "1px solid #1c2736", borderRadius: 4, padding: "20px 22px", marginBottom: 16 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#fff533", textTransform: "uppercase", marginBottom: 10 }}>Create Your Link</div>
                  <p style={{ fontSize: 12, color: "#8899aa", lineHeight: 1.7, marginBottom: 14 }}>
                    Pick a short code — it becomes your link (e.g. <span style={{ fontFamily: "DM Mono, monospace", color: "#ccd0d8" }}>/r/jane10</span>). These three details are the only things stored — no email, no account.
                  </p>
                  <input
                    className="admin-input"
                    type="text"
                    placeholder="Your name or handle"
                    maxLength={40}
                    value={refName}
                    onChange={(e) => { setRefName(e.target.value); setRefFormError(""); }}
                    style={{ marginBottom: 10, fontSize: 13 }}
                  />
                  <input
                    className="admin-input"
                    type="text"
                    placeholder="Where will you share it? (e.g. YouTube, Discord, family)"
                    maxLength={60}
                    value={refChannels}
                    onChange={(e) => { setRefChannels(e.target.value); setRefFormError(""); }}
                    style={{ marginBottom: 10, fontSize: 13 }}
                  />
                  <input
                    className="admin-input"
                    type="text"
                    placeholder="Short code (e.g. jane10)"
                    maxLength={24}
                    value={refShort}
                    onChange={(e) => { setRefShort(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, "")); setRefFormError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && !refCreating && handleCreateReferral()}
                    style={{ marginBottom: 10, fontSize: 13 }}
                  />
                  {refShort && (
                    <div style={{ fontSize: 11, color: "#556070", fontFamily: "DM Mono, monospace", marginBottom: 10 }}>
                      Your link: <span style={{ color: "#fff533" }}>u-operator.com/r/{refShort}</span>
                    </div>
                  )}
                  {refFormError && <div style={{ fontSize: 11, color: "#ff5566", marginBottom: 10 }}>{refFormError}</div>}
                  <button className="claim-btn" style={{ width: "100%", padding: "14px 20px", fontSize: 12 }} onClick={handleCreateReferral} disabled={refCreating}>
                    {refCreating ? "Creating…" : "Create My Referral Link"}
                  </button>
                </div>

                {/* Lookup */}
                <div style={{ background: "#131a26", border: "1px solid #1c2736", borderRadius: 4, padding: "20px 22px" }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#8899aa", textTransform: "uppercase", marginBottom: 10 }}>Already Have a Link?</div>
                  <p style={{ fontSize: 12, color: "#8899aa", lineHeight: 1.7, marginBottom: 14 }}>
                    On a new device or cleared your browser? Enter your short code (the last part of your link, e.g. <span style={{ fontFamily: "DM Mono, monospace", color: "#ccd0d8" }}>jane10</span>) to load your stats.
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      className="admin-input"
                      type="text"
                      placeholder="your short code"
                      value={lookupKey}
                      onChange={(e) => { setLookupKey(e.target.value); setLookupError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && !lookupBusy && handleLookup()}
                      style={{ flex: 1, fontSize: 13 }}
                    />
                    <button className="admin-btn" onClick={handleLookup} disabled={lookupBusy || !lookupKey.trim()}>
                      {lookupBusy ? "…" : "Find"}
                    </button>
                  </div>
                  {lookupError && <div style={{ fontSize: 11, color: "#ff5566", marginTop: 10 }}>{lookupError}</div>}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
